import { CartAddEvent } from '@theme/events';

/**
   * AddCartButton，加购按钮，点击元素本身触发
   * 
   * Attributes:
   * @param {String} data-variant-id 产品变体 ID
   * @param {String} data-quantity 购买数量，可选，默认为1
   * 
   * @example 
   * ```html
   * <add-cart-button data-variant-id="{{ product.first_available_variant.id }}" data-quantity="1" class="button round">
   *   Add to cart
   *   {%- render 'loading-spinner' -%}
   * </add-cart-button>
   * ```
   */
if (!customElements.get('add-cart-button')) {
    class AddCartButton extends HTMLElement {
        // 标记
        _marks = {
            loading: "loading"
        };

        /** @type {{ loadingSpinner: Element | null } | undefined} */
        elements;

        _shopify_features_script = document.querySelector("script[id='shopify-features']");
        _shopify_features_json = JSON.parse(this._shopify_features_script?.innerHTML || '{}');
        _checkout_headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'authorization': 'Basic ' + btoa(this._shopify_features_json.accessToken)
        };

        /** @returns {string | null} */
        get _variantId() { return this.getAttribute("data-variant-id") }
        get _quantity() { return this.getAttribute("data-quantity") ?? 1 }

        /**
         * 购物车区块的 section id，随加购请求一起提交后，
         * Shopify 会在回包里带上这些区块的最新 HTML，抽屉才能刷新内容
         */
        get _sectionIds() {
            /** @type {string[]} */
            const ids = [];
            document.querySelectorAll('cart-items-component').forEach((item) => {
                if (item instanceof HTMLElement && item.dataset.sectionId) {
                    ids.push(item.dataset.sectionId);
                }
            });
            return ids;
        }

        constructor() {
            super();
        }

        connectedCallback() {
            this.elements = {
                loadingSpinner: this.querySelector(".loading__spinner"),
            };
            this.addEventListener('click', (e) => {
                this.startLoading();
                this.addToCart().finally(() => {
                    this.finishLoading();
                });
                
            });
        }

        /**
         * 加购
         */
        async addToCart() {
            const variantId = this._variantId;
            if (!variantId) return;

            const variantIds = variantId.split(',');

            const items = variantIds.map(id => {
                return {
                    'form_type': "product",
                    'utf8': "✓",
                    'id': parseInt(id, 10),
                    'quantity': this._quantity,
                };
            });

            let data_json = {
                'items': items,
                'sections': this._sectionIds.join(','),
            };

            let data = JSON.stringify(data_json);
            return fetch("/cart/add.json",
                {
                    method: 'POST',
                    headers: this._checkout_headers,
                    body: data
                }
            ).then((response) => {
                if (response.ok) {
                    return response.json();
                }
            }).then((response) => {
                return this.updateCart(response);
            }).catch((err) => {
                console.error(err);
            });
        }

        /**
         * 更新购物车，并弹出侧边购物车
         * @param {{ sections?: Record<string, string>, item_count?: number } | undefined} response
         */
        async updateCart(response) {
            /**
             * 加购失败时 response 是 undefined，这时既不广播也不弹抽屉，
             * 否则 cart:update 会带着 itemCount 0 把购物车角标刷没
             */
            if (!response) return;

            /**
             * /cart/add.json 回包不是完整 cart；再拉一次 /cart.js，
             * 让 cart:update 带上 resource.items，避免全页 product-form 各自刷 /cart.js
             */
            /** @type {{ item_count?: number, items?: unknown[] }} */
            let cart = {};
            try {
                const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
                if (cartRes.ok) cart = await cartRes.json();
            } catch (err) {
                console.error(err);
            }

            /**
             * /cart.js 失败时 cart 为空，不能用 itemCount: 0（会刷掉角标）。
             * add.json 也不含购物车总件数，改用 sessionStorage 缓存 + 本次加购量估算。
             */
            /** @type {number | undefined} */
            let itemCount = Number(cart.item_count);
            if (!Number.isFinite(itemCount)) {
                try {
                    const cached = JSON.parse(sessionStorage.getItem('cart-count') || '{}');
                    const prev = Number(cached.value) || 0;
                    const qty = Number(this._quantity) || 1;
                    const variants = String(this._variantId || '')
                        .split(',')
                        .filter(Boolean).length || 1;
                    itemCount = prev + qty * variants;
                } catch {
                    itemCount = undefined;
                }
            }

            this.dispatchEvent(
                new CartAddEvent(cart, this.id, {
                    source: 'add-cart-button',
                    ...(itemCount != null ? { itemCount } : {}),
                    variantId: this._variantId ?? undefined,
                    sections: response.sections ?? {},
                })
            );

            this.openCartDrawer();
        }

        /**
         * 弹出侧边购物车
         */
        openCartDrawer() {
            const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
            if (!drawer) return;
            if (typeof drawer.open === 'function') {
                drawer.open();
                return;
            }
            if (typeof drawer.showDialog === 'function') {
                drawer.showDialog();
            }
        }

        startLoading() {
            this.setAttribute("aria-disabled", "true");
            this.classList.add(this._marks.loading);
            this.elements?.loadingSpinner?.classList.remove("hidden");
        }

        finishLoading() {
            this.removeAttribute("aria-disabled");
            this.classList.remove(this._marks.loading);
            this.elements?.loadingSpinner?.classList.add("hidden");
        }
    }

    customElements.define('add-cart-button', AddCartButton);
}